import type { ExtendedMove } from "@/types/types"
import type { Game } from "@/types/serializers"
import { startFen } from "../constants.ts"
import { captureEvent } from "@/lib/posthog"

const symbolToNag: Record<string, string> = {
  "!": "$1",
  "?": "$2",
  "!!": "$3",
  "??": "$4",
  "!?": "$5",
  "?!": "$6",
}

const toPgnDate = (date: string | null): string => {
  if (!date) return "????.??.??"

  if (/^\d{4}\.\d{2}\.\d{2}$/.test(date)) return date

  const m = date.match(/^(\d{4})-(\d{2})-(\d{2})/)
  if (m) return `${m[1]}.${m[2]}.${m[3]}`

  return "????.??.??"
}

const headersToText = (tags: Record<string, string>): string =>
  Object.entries(tags)
    .filter(([, v]) => v != null && v !== "")
    .map(([k, v]) => `[${k} "${v}"]`)
    .join("\n")

type NumState = { turn: "w" | "b"; fullmove: number }

const stateFromFen = (fen: string | undefined): NumState => {
  if (!fen) return { turn: "w", fullmove: 1 }
  const p = fen.split(" ")
  const turn = (p[1] as "w" | "b") || "w"
  const fullmove = Number(p[5]) || 1
  return { turn, fullmove }
}

const afterMove = (s: NumState): NumState =>
  s.turn === "w"
    ? { turn: "b", fullmove: s.fullmove }
    : { turn: "w", fullmove: s.fullmove + 1 }

const serializeLine = (line: ExtendedMove[], st: NumState): string => {
  let out: string[] = []
  let cur = { ...st }

  let pendingForWhite: string[] = []
  let pendingForBlack: string[] = []

  const flushPendingAfterThisSan = () => {
    if (cur.turn === "w") {
      if (pendingForWhite.length) {
        for (const r of pendingForWhite) out.push(r)
        pendingForWhite = []
      }
    } else {
      if (pendingForBlack.length) {
        for (const r of pendingForBlack) out.push(r)
        pendingForBlack = []
      }
    }
  }

  for (let i = 0; i < line.length; i++) {
    const move = line[i]

    if (cur.turn === "w") {
      out.push(`${cur.fullmove}.`)
    } else if (i === 0) {
      out.push(`${cur.fullmove}...`)
    }

    out.push(move.san)

    flushPendingAfterThisSan()

    const next = afterMove(cur)

    if (move.moves?.length) {
      const bucket = next.turn === "w" ? pendingForWhite : pendingForBlack
      for (const alt of move.moves) {
        const rav = serializeLine(alt, { ...next })
        bucket.push(`(${rav})`)
      }
    }

    if (move.annotation) out.push(symbolToNag[move.annotation])

    if (move.comment?.trim()) out.push(`{${move.comment.trim()}}`)

    cur = next
  }

  if (pendingForWhite.length) {
    for (const r of pendingForWhite) out.push(r)
  }
  if (pendingForBlack.length) {
    for (const r of pendingForBlack) out.push(r)
  }

  return out.join(" ").replace(/\s+/g, " ").trim()
}

export function generatePgn({
  date,
  event,
  event_round,
  start,
  result,
  white_player,
  white_elo,
  black_player,
  black_elo,
  moves,
  chess960,
}: Game) {
  const tags: Record<string, string> = {
    Event: event?.name ?? "?",
    Site: event?.location ?? "?",
    Date: toPgnDate(date),
    Round: event_round != null ? String(event_round) : "?",
    White: white_player?.name ?? "?",
    Black: black_player?.name ?? "?",
    Result: result ?? "*",
  }
  if (white_player?.title) tags.WhiteTitle = white_player.title
  if (white_elo) tags.WhiteElo = String(white_elo)
  if (white_player?.fide_id) tags.WhiteFideId = String(white_player.fide_id)
  if (black_player?.title) tags.BlackTitle = black_player.title
  if (black_elo) tags.BlackElo = String(black_elo)
  if (black_player?.fide_id) tags.BlackFideId = String(black_player.fide_id)
  if (start && start !== startFen) {
    tags.FEN = start
    tags.SetUp = "1"
  }
  if (chess960) {
    tags.Variant = "Chess960"
    if (start && !tags.FEN) {
      tags.FEN = start
      tags.SetUp = "1"
    }
  }

  const mainline: ExtendedMove[] =
    Array.isArray(moves) && Array.isArray(moves[0]) ? moves[0] : []

  const movetext = serializeLine(mainline, stateFromFen(start))
  const headerText = headersToText(tags)

  return `${headerText}\n\n${movetext} ${tags.Result}`.trim()
}

const gamesToPgn = (games: Game[]) => games.map(generatePgn).join("\n\n")

const downloadPgn = (games: Game[], fileName: string) => {
  captureEvent("game_exported", { game_count: games.length })

  const pgn = gamesToPgn(games)

  const blob = new Blob([pgn], { type: "text/plain" })

  // Create a temporary anchor element
  const a = document.createElement("a")
  a.href = URL.createObjectURL(blob)
  a.download = `${fileName}.pgn`

  // Append the anchor to the body, trigger the download, then remove the anchor
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
}

export default downloadPgn
